import React, { useCallback, useEffect, useState } from "react";
import { ModalDialog, StringField, SelectField, Button, Form, TextField, Tooltip } from "@culturehq/components";

import { useDoor } from "app/hooks";
import { t, Trans } from "app/utils";

import ChqModalDialog from "components/common/ChqModalDialog";
import StatusChangeConfirmModal from "./StatusChangeConfirmModal";

export const allowedMedia = [
  { label: t("Both"), value: "all" },
  { label: t("Images Only"), value: "just_images" },
  { label: t("Videos Only"), value: "just_videos" }
];

const SubPromptModal = ({ children, storyQuestion, parentStoryQuestion, onCreate, onUpdate }) => {
  const [changed, setChanged] = useState(false);
  const onChange = useCallback(() => setChanged(true), [setChanged]);
  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the prompt modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) {
        setChanged(false);
        onClose();
        setPrompt(storyQuestion?.question);
        setAllowedType(storyQuestion?.allowedTypes || allowedMedia[0].value);
      }
    },
    [changed, onClose, storyQuestion]
  );
  const { open, onOpen, onClose } = useDoor(false);
  const [prompt, setPrompt] = useState(storyQuestion?.question);
  const [instructions, setInstructions] = useState(storyQuestion?.summary);
  const [allowedType, setAllowedType] = useState(
    storyQuestion?.allowedTypes || allowedMedia[0].value
  );
  const parentStoryQuestionStatus = parentStoryQuestion?.status;
  const isParentTrendHidden = storyQuestion?.parentStoryQuestion?.status === "hidden" || parentStoryQuestionStatus === "hidden";
  const [status, setStatus] = useState(isParentTrendHidden ? "hidden" : (storyQuestion?.status || "active"));
  const [savedStatus, setSavedStatus] = useState(isParentTrendHidden ? "hidden" : (storyQuestion?.status || parentStoryQuestionStatus || "active"));

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [valuesToSubmit, setValuesToSubmit] = useState(null);

  useEffect(
    () => {
      if (open) {
        setInstructions(storyQuestion?.instructions || storyQuestion?.summary);
        setStatus(isParentTrendHidden ? "hidden" : (storyQuestion?.status || "active"));
        setSavedStatus(isParentTrendHidden ? "hidden" : (storyQuestion?.status || "active"));
        setPrompt(storyQuestion?.question);
        setAllowedType(storyQuestion?.allowedTypes || allowedMedia[0].value);
      }
    }, [open, storyQuestion, isParentTrendHidden]
  );

  const handleSaveSubprompt = () => {
    if (prompt?.length > 0 && allowedType && status) {
      const updatedStoryQuestion = {
        ...storyQuestion,
        allowedTypes: allowedType,
        question: prompt,
        instructions,
        status: isParentTrendHidden ? "hidden" : status
      };

      if (storyQuestion?.status === "hidden" && status === "active") {
        setValuesToSubmit(updatedStoryQuestion);
        setShowConfirmModal(true);
        return;
      }

      if (status === "archived" && storyQuestion?.answers > 0 && storyQuestion?.status !== "archived") {
        const confirmMessage = t(`The stories in this ${storyQuestion.storyQuestionType === "parent_prompt" ? "trend" : "prompt"} will be archived automatically. Do you want to continue?`);
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      if (status === "active" && storyQuestion?.answers > 0 && storyQuestion?.status === "archived") {
        const confirmMessage = t("In changing to Active status, any associated stories may now be moved out of Archive in the Admin Review tab");
        // eslint-disable-next-line no-alert
        if (!window.confirm(confirmMessage)) {
          onClose();
          return;
        }
      }

      handleSubmit(updatedStoryQuestion);
    }
  };

  const handleSubmit = useCallback(values => {
    setShowConfirmModal(false);
    setValuesToSubmit(null);
    if (storyQuestion) {
      onUpdate(values);
      setSavedStatus(values.status);
    } else {
      onCreate({
        allowedTypes: values.allowedTypes,
        question: values.question,
        instructions: values.instructions,
        key: new Date().getTime(),
        position: 100,
        status: values.status
      });
      setPrompt("");
      setInstructions("");
      setAllowedType(allowedMedia[0].value);
    }
    setChanged(false);
    onClose();
  }, [storyQuestion, onUpdate, onCreate, onClose]);

  const handleCancelSubmit = useCallback(() => {
    setShowConfirmModal(false);
    setValuesToSubmit(null);
  }, []);

  const handleInstructionsChange = value => {
    onChange();
    setInstructions(value);
  };

  const handlePromptChange = value => {
    onChange();
    setPrompt(value);
  };

  const handleAllowedTypeChange = value => {
    onChange();
    setAllowedType(value);
  };

  const handleStatusChange = value => {
    onChange();
    setStatus(value);
  };

  const isHidden = savedStatus === "hidden";
  const isParentTrendActive = storyQuestion?.parentStoryQuestion?.status === "active" || parentStoryQuestionStatus === "active";
  const canEditHiddenPrompt = isHidden && isParentTrendActive;
  const options = [
    { value: "active", label: t("Active") },
    { value: "paused", label: t("Paused") },
    { value: "hidden", label: t("Hidden") },
    { value: "archived", label: t("Archived") }
  ];

  const selectField = (
    <SelectField
      name="status"
      required
      value={status}
      options={options}
      onChange={handleStatusChange}
      disabled={isHidden && !canEditHiddenPrompt}
      valueLabel={isHidden ? t("Hidden") : undefined}
    >
      <Trans>Status</Trans>
    </SelectField>
  );

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog
          onClose={onConfirmClose}
          className="story-question-modal"
        >
          <ModalDialog.Heading onClose={onConfirmClose}>
            {storyQuestion ? (
              <Trans>Edit Prompt</Trans>
            ) : (
              <Trans>Add a Prompt</Trans>
            )}
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <Form initialValues={{ typesAllowed: "all", SubpromptTitle: "" }}>
              <StringField
                name="SubpromptTitle"
                autoFocus
                required
                value={prompt}
                onChange={handlePromptChange}
              >
                <Trans>Prompt</Trans>
              </StringField>
              <SelectField
                className="AllowedTypes"
                default
                options={allowedMedia}
                required
                value={allowedType}
                onChange={handleAllowedTypeChange}
              >
                <Trans>Media Types Allowed</Trans>
              </SelectField>
              {isHidden && !canEditHiddenPrompt ? (
                <Tooltip
                  right
                  top
                  tip={(
                    <span className="CustomTooltipText">
                      {t("You can not edit the status of a prompt in a hidden trend")}
                    </span>
                  )}
                >
                  {selectField}
                </Tooltip>
              ) : (
                selectField
              )}
              <TextField
                name="instructions"
                value={instructions}
                onChange={handleInstructionsChange}
              >
                <Trans>Instructions</Trans>
              </TextField>
              <Button primary onClick={handleSaveSubprompt}>
                <Trans>Save</Trans>
              </Button>
            </Form>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      {showConfirmModal && (
        <StatusChangeConfirmModal
          onCancelSubmit={handleCancelSubmit}
          onSubmit={handleSubmit}
          showConfirmModal={showConfirmModal}
          values={valuesToSubmit}
        />
      )}
    </>
  );
};

export default SubPromptModal;
